import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, DollarSign, Eye, ShoppingCart,
} from 'lucide-react';
import { KpiCard, LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingOrder, setEditingOrder] = useState(null);
  const [updateForm, setUpdateForm] = useState({ status: '', note: '', tracking_number: '', carrier: 'USPS' });

  const token = sessionStorage.getItem('admin_token');

  const fetchOrders = useCallback(async () => {
    try {
      const url = statusFilter ? `${API_URL}/api/admin/orders?status=${statusFilter}` : `${API_URL}/api/admin/orders`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.ok) setOrders(await resp.json());
      const statsResp = await fetch(`${API_URL}/api/admin/orders/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (statsResp.ok) setOrderStats(await statsResp.json());
    } catch (err) { console.error('Admin orders fetch failed:', err); }
    setLoading(false);
  }, [token, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateOrderStatus = async () => {
    if (!editingOrder || !updateForm.status) return;
    await fetch(`${API_URL}/api/admin/orders/${editingOrder}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(updateForm),
    });
    setEditingOrder(null);
    setUpdateForm({ status: '', note: '', tracking_number: '', carrier: 'USPS' });
    fetchOrders();
  };

  if (loading) return <LoadingSkeleton />;

  const statusColors = {
    pending: 'bg-gray-100 text-gray-600',
    confirmed: 'bg-amber-50 text-amber-600',
    shipped: 'bg-blue-50 text-blue-600',
    delivered: 'bg-green-50 text-green-600',
    cancelled: 'bg-red-50 text-red-600',
  };

  return (
    <div className="space-y-6" data-testid="orders-tab">
      {/* KPIs */}
      {orderStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard icon={ShoppingCart} label="Total Orders" value={orderStats.total_orders} color="#8B5CF6" />
          <KpiCard icon={DollarSign} label="Revenue" value={`$${orderStats.total_revenue?.toLocaleString()}`} color="#10B981" />
          <KpiCard icon={Activity} label="Pending" value={orderStats.pending} color="#F59E0B" />
          <KpiCard icon={Eye} label="Shipped" value={orderStats.shipped} color="#06B6D4" />
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 items-center">
        <p className="text-xs text-gray-500 mr-2">Filter:</p>
        {['', 'pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${statusFilter === s ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            data-testid={`order-filter-${s || 'all'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="admin-orders-list">
        {orders.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-12">No orders found</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Total</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const oid = order.id || order.order_id || '?';
                return (
                  <tr key={oid} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-testid={`admin-order-${oid}`}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">#{oid.slice(0, 8).toUpperCase()}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{order.customer_email || order.customer_name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-500'}`}>
                        {order.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">${(order.total || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => { setEditingOrder(oid); setUpdateForm({ status: order.status || '', note: '', tracking_number: order.tracking_number || '', carrier: order.carrier || 'USPS' }); }}
                        className="text-xs text-teal-600 hover:text-teal-800" data-testid={`edit-order-${oid}`}>
                        Update
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Update Order Modal */}
      {editingOrder && (
        <div className="bg-white rounded-xl border border-teal-200 p-5 space-y-4" data-testid="order-update-form">
          <h3 className="text-sm font-semibold text-gray-900">Update Order #{editingOrder.slice(0, 8).toUpperCase()}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Status</label>
              <select className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={updateForm.status} onChange={e => setUpdateForm({...updateForm, status: e.target.value})} data-testid="order-status-select">
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Note</label>
              <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={updateForm.note} onChange={e => setUpdateForm({...updateForm, note: e.target.value})} placeholder="Optional note" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Tracking Number</label>
              <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={updateForm.tracking_number} onChange={e => setUpdateForm({...updateForm, tracking_number: e.target.value})} placeholder="e.g. 9400111..." />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Carrier</label>
              <select className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={updateForm.carrier} onChange={e => setUpdateForm({...updateForm, carrier: e.target.value})}>
                <option value="USPS">USPS</option>
                <option value="UPS">UPS</option>
                <option value="FedEx">FedEx</option>
                <option value="DHL">DHL</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={updateOrderStatus} className="px-4 py-2 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700" data-testid="order-update-submit">Update Status</button>
            <button onClick={() => setEditingOrder(null)} className="px-4 py-2 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
